import { useEffect, useRef, useState } from "react";
import {
  Animated,
  Pressable,
  Text,
  useWindowDimensions,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER, semanticColors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";

export type MoonSegmentedOption<T extends string> = {
  value: T;
  label: string;
  shortLabel?: string;
  icon?: LucideIcon;
  /**
   * What a screen reader says for this option when it should say more than
   * the visible label (or when the control shows icons only). Defaults to
   * the label.
   */
  accessibilityLabel?: string;
  /** A screen reader hint for this option, such as what choosing it does. */
  accessibilityHint?: string;
  /**
   * Stable analytics identifier for a press on this option. Defaults to
   * `fact_sheet_tab_<value>`; pass one where the value must not travel
   * (a personal fact) or a funnel already groups on an existing id.
   */
  action?: string;
};

// The default tone sits on a light surface; the inverse tone sits on a dark
// one (the silk brown calendar in check-in mode, the sperm analysis card).
const SEGMENTED_TONES = {
  default: {
    track: "rgba(42,37,34,0.06)",
    indicator: "rgba(255,255,255,0.78)",
    indicatorBorder: "rgba(42,37,34,0.08)",
    pressed: "rgba(255,255,255,0.35)",
    activeText: EMBER.colors.text,
    inactiveText: EMBER.colors.textLight,
  },
  inverse: {
    track: semanticColors.onDark.fill.subtle,
    indicator: "rgba(255,247,239,0.92)",
    indicatorBorder: "rgba(255,247,239,0.92)",
    pressed: semanticColors.onDark.fill.strong,
    activeText: "#5A3125",
    inactiveText: semanticColors.onDark.text.secondary,
  },
} as const;

// Fit mode: each option hugs its label with even padding, and the indicator
// covers exactly the chosen option, so it never crowds the next label.
const FIT_TAB_PADDING = 11;
// The rounded shape: a compact rectangle (the Care calendar's Week/Month
// toggle), for units inside a field and small view switches.
const ROUNDED_TAB_PADDING = 10;

/** Segmented control: one track, a sliding white indicator. */
export function MoonSegmentedControl<T extends string>({
  active,
  onChange,
  tabs,
  fit: fitProp = false,
  shape = "pill",
  tone = "default",
  optionRole = "tab",
  accessibilityLabel,
  iconOnly = false,
  disabled = false,
  style,
}: {
  /** The chosen option, or null while nothing has been chosen yet. */
  active: T | null;
  onChange: (tab: T) => void;
  tabs: MoonSegmentedOption<T>[];
  /**
   * Size each option to its label instead of sharing the width equally.
   * Use for short choices such as units.
   */
  fit?: boolean;
  /**
   * `pill` is the default fully rounded control. `rounded` is a compact
   * rectangle sized to its labels: use it for units inside a field and for
   * small view switches such as Week/Month.
   */
  shape?: "pill" | "rounded";
  /** `inverse` is for a dark surface: a cream pill and cream labels. */
  tone?: "default" | "inverse";
  /**
   * How assistive tech announces the options. `tab` switches what is shown;
   * `radio` picks a value the form saves, inside a labelled radiogroup.
   */
  optionRole?: "tab" | "radio";
  /** Names the group. Use it with `optionRole="radio"`. */
  accessibilityLabel?: string;
  /**
   * Show each option's icon alone, larger, with its label as the accessible
   * name. For choices whose icons speak for themselves.
   */
  iconOnly?: boolean;
  /** Ignore presses and dim the control, for example while a form saves. */
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const palette = SEGMENTED_TONES[tone];
  const rounded = shape === "rounded";
  // The rounded shape always hugs its labels.
  const fit = fitProp || rounded;
  const trackRadius = rounded ? 7 : 999;
  // A form answer (Yes/No, partner/donor) reads at body size with a clearly
  // darker unselected label; view tabs and units stay compact.
  const answerSize = optionRole === "radio" && !rounded;
  const inactiveLabel =
    answerSize && tone === "default"
      ? EMBER.colors.textMid
      : palette.inactiveText;
  const optionRadius = rounded ? 5 : 999;
  const capturePress = usePressCapture("MoonSegmentedControl");
  const { width } = useWindowDimensions();
  const compactTabs = width < 420;
  const [controlWidth, setControlWidth] = useState(0);
  const matchedIndex = tabs.findIndex((tab) => tab.value === active);
  // With nothing chosen there is no indicator; the first choice then places
  // it without sliding in from another option.
  const hasActive = matchedIndex >= 0;
  const activeIndex = Math.max(0, matchedIndex);
  const indicatorProgress = useRef(new Animated.Value(activeIndex)).current;
  const hadActive = useRef(hasActive);
  const [tabLayouts, setTabLayouts] = useState<
    Record<number, { x: number; width: number }>
  >({});
  const controlPadding = rounded ? 2 : 3;
  const tabGap = fit ? 0 : 4;
  const indicatorWidth =
    tabs.length > 0
      ? (controlWidth - controlPadding * 2 - tabGap * (tabs.length - 1)) /
        tabs.length
      : 0;
  const fitBoxes = tabs.map((_, index) => {
    const layout = tabLayouts[index];
    return layout ? { left: layout.x, width: layout.width } : null;
  });
  const fitMeasured = fit && fitBoxes.every((box) => box !== null);

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const appearing = hasActive && !hadActive.current;
    hadActive.current = hasActive;
    if (reducedMotion || appearing) {
      indicatorProgress.setValue(activeIndex);
      return;
    }
    Animated.timing(indicatorProgress, {
      toValue: activeIndex,
      duration: 190,
      // Fit mode animates the indicator's width, which the native driver
      // can't drive.
      useNativeDriver: !fit,
    }).start();
  }, [activeIndex, fit, hasActive, indicatorProgress, reducedMotion]);

  const indicatorTranslateX =
    tabs.length > 1 && indicatorWidth > 0
      ? indicatorProgress.interpolate({
          inputRange: tabs.map((_, index) => index),
          outputRange: tabs.map(
            (_, index) => index * (indicatorWidth + tabGap),
          ),
          extrapolate: "clamp",
        })
      : 0;
  const interpolateFit = (key: "left" | "width") =>
    tabs.length > 1
      ? indicatorProgress.interpolate({
          inputRange: tabs.map((_, index) => index),
          outputRange: fitBoxes.map((box) => box?.[key] ?? 0),
          extrapolate: "clamp",
        })
      : (fitBoxes[0]?.[key] ?? 0);

  return (
    <View
      onLayout={(event) => setControlWidth(event.nativeEvent.layout.width)}
      accessibilityRole={optionRole === "radio" ? "radiogroup" : undefined}
      accessibilityLabel={accessibilityLabel}
      style={[
        {
          flexDirection: "row",
          gap: tabGap,
          borderRadius: trackRadius,
          backgroundColor: palette.track,
          padding: controlPadding,
          marginTop: 16,
          position: "relative",
          overflow: "hidden",
          opacity: disabled ? 0.5 : 1,
        },
        style,
      ]}
    >
      {hasActive && fitMeasured ? (
        <Animated.View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: controlPadding,
            bottom: controlPadding,
            borderRadius: optionRadius,
            ...(rounded
              ? {
                  // A lifted white tab, as the Week/Month toggle drew it.
                  backgroundColor:
                    tone === "default" ? "#FFFFFF" : palette.indicator,
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 1 },
                  shadowOpacity: 0.08,
                  shadowRadius: 3,
                }
              : {
                  backgroundColor: palette.indicator,
                  borderWidth: 1,
                  borderColor: palette.indicatorBorder,
                }),
            ...(reducedMotion
              ? fitBoxes[activeIndex]
              : {
                  left: interpolateFit("left"),
                  width: interpolateFit("width"),
                }),
          }}
        />
      ) : null}
      {hasActive && !fit && indicatorWidth > 0 && (
        <Animated.View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: controlPadding,
            bottom: controlPadding,
            width: indicatorWidth,
            borderRadius: 999,
            backgroundColor: palette.indicator,
            borderWidth: 1,
            borderColor: palette.indicatorBorder,
            // A transformed indicator is composited on web, and the track's
            // rounded clip then re-rasterizes differently from frame to frame.
            // Without motion there is nothing to slide, so place it in layout.
            ...(reducedMotion
              ? {
                  left:
                    controlPadding + activeIndex * (indicatorWidth + tabGap),
                }
              : {
                  left: controlPadding,
                  transform: [{ translateX: indicatorTranslateX }],
                }),
          }}
        />
      )}
      {tabs.map((tab, index) => {
        const selected = active === tab.value;
        const Icon = tab.icon;
        const showLabel = !(iconOnly && Icon);
        return (
          <Pressable
            key={tab.value}
            onPress={() => {
              capturePress(
                pressLabel(tab.label, tab.shortLabel),
                tab.action ?? `fact_sheet_tab_${tab.value}`,
              );
              onChange(tab.value);
            }}
            disabled={disabled}
            accessibilityLabel={
              tab.accessibilityLabel ?? (showLabel ? undefined : tab.label)
            }
            accessibilityHint={tab.accessibilityHint}
            // react-native-web 0.21 ignores accessibilityState, so the state
            // is mirrored as the matching aria attribute for the DOM.
            {...(optionRole === "radio"
              ? {
                  accessibilityRole: "radio" as const,
                  accessibilityState: { checked: selected, disabled },
                  "aria-checked": selected,
                }
              : {
                  accessibilityRole: "tab" as const,
                  accessibilityState: { selected, disabled },
                  "aria-selected": selected,
                })}
            onLayout={
              fit
                ? (event) => {
                    const { x, width: tabWidth } = event.nativeEvent.layout;
                    setTabLayouts((current) =>
                      current[index]?.x === x &&
                      current[index]?.width === tabWidth
                        ? current
                        : { ...current, [index]: { x, width: tabWidth } },
                    );
                  }
                : undefined
            }
            style={({ pressed }) => ({
              ...(fit ? null : { flex: 1 }),
              minHeight: rounded ? 23 : answerSize ? 38 : 32,
              borderRadius: optionRadius,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              gap: answerSize ? 6 : 3,
              paddingHorizontal: rounded
                ? ROUNDED_TAB_PADDING
                : fit
                  ? FIT_TAB_PADDING
                  : 2,
              backgroundColor: pressed ? palette.pressed : "transparent",
              zIndex: 1,
            })}
          >
            {Icon ? (
              <Icon
                size={showLabel ? (answerSize ? 15 : 12) : 20}
                color={selected ? palette.activeText : inactiveLabel}
                strokeWidth={showLabel ? 1.8 : 1.65}
              />
            ) : null}
            {showLabel ? (
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                adjustsFontSizeToFit
                style={{
                  fontSize: answerSize ? 13 : 11,
                  lineHeight: answerSize ? 17 : 14,
                  fontWeight: answerSize
                    ? selected
                      ? "700"
                      : "500"
                    : selected
                      ? "800"
                      : "700",
                  color: selected ? palette.activeText : inactiveLabel,
                }}
              >
                {compactTabs ? (tab.shortLabel ?? tab.label) : tab.label}
              </Text>
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
}

export default MoonSegmentedControl;
